import React, { forwardRef } from 'react';
import { logoFlyerSrc } from '../utils/imagenesFlyer';

const NAVY = '#0f3b66';
const NAVY_DARK = '#0a2742';
const BLUE = '#2b7cd6';
const LIGHT_BLUE = '#5dade2';
const SOFT = '#eef4fb';
const GOLD = '#d4af37';
const CLOVER = '#2e8b57';
const GRAY = '#6b7280';

function obtenerPremios(rifa) {
  if (Array.isArray(rifa.premios) && rifa.premios.length) return rifa.premios;
  if (rifa.premio) return rifa.premio.split(',').map((p) => p.trim()).filter(Boolean);
  return ['Gran premio'];
}

function urlImagenPremio(rifa, index) {
  const dataUrls = rifa.imagenesPremiosDataUrls;
  if (Array.isArray(dataUrls) && dataUrls[index]) return dataUrls[index];
  return null;
}

function formatearFechaCreacion(rifa) {
  const raw = rifa.fechaCreacion || rifa.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function estadoNumero(numero, rifa, numerosGanadores) {
  if (numerosGanadores.has(numero)) return 'ganador';
  const vendido = (rifa.numerosReservados || []).includes(numero);
  const pendiente = (rifa.numerosPendientes || []).includes(numero);
  if (vendido || pendiente) return 'ocupado';
  return 'libre';
}

function TrebolOcupado() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="7" r="4.2" fill={CLOVER} />
      <circle cx="7" cy="13" r="4.2" fill={CLOVER} />
      <circle cx="17" cy="13" r="4.2" fill={CLOVER} />
      <path d="M12 14v7" stroke={CLOVER} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

const RifaFlyerCompartir = forwardRef(function RifaFlyerCompartir({ rifa, ganadores = [] }, ref) {
  const total = parseInt(rifa.totalNumeros, 10) || 0;
  const premios = obtenerPremios(rifa).slice(0, 3);
  const numerosGanadores = new Set(
    (ganadores || []).filter((g) => g.rifaId === rifa.id).map((g) => g.numeroGanador)
  );
  const numeros = Array.from({ length: total }, (_, i) => i + 1);
  const cols = total <= 50 ? 10 : total <= 100 ? 10 : 12;
  const esSorteo = rifa.tipo === 'sorteo';
  const precioNum = Number(rifa.precio) || 0;
  const precioTxt = esSorteo ? 'GRATIS' : `$${precioNum.toLocaleString('es-AR')}`;
  const ocupados = new Set([
    ...(rifa.numerosReservados || []),
    ...(rifa.numerosPendientes || []),
  ]);
  const disponibles = Math.max(0, total - ocupados.size);
  const tituloMostrar = (rifa.titulo || 'Rifa').toUpperCase();
  const fechaCreacionTxt = formatearFechaCreacion(rifa);
  const logoSrc = rifa.logoDataUrl || rifa.logoSrc || logoFlyerSrc;

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        position: 'relative',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        background: '#ffffff',
        color: NAVY,
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {/* Encabezado con logo a la izquierda */}
      <div
        style={{
          background: `linear-gradient(135deg, #ffffff 0%, ${SOFT} 100%)`,
          padding: '28px 40px 22px',
          borderBottom: `4px solid ${BLUE}`,
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
          {/* Logo a la izquierda */}
          <div
            style={{
              flexShrink: 0,
              width: 220,
              minHeight: 180,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <img
              src={logoSrc}
              alt="Acceso7"
              style={{
                maxHeight: 180,
                maxWidth: 220,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </div>

          {/* Título centrado */}
          <div style={{ flex: 1, textAlign: 'center' }}>
            <p
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: 8,
                color: BLUE,
                textTransform: 'uppercase',
              }}
            >
              Sorteo
            </p>
            <p
              style={{
                margin: '4px 0 0',
                fontSize: 64,
                fontWeight: 900,
                letterSpacing: 4,
                color: NAVY,
                lineHeight: 1,
                fontFamily: "'Georgia', 'Times New Roman', serif",
              }}
            >
              RIFA
            </p>
            <p
              style={{
                margin: '10px 0 0',
                fontSize: 24,
                fontWeight: 800,
                color: NAVY_DARK,
                letterSpacing: 1.5,
                lineHeight: 1.2,
              }}
            >
              {tituloMostrar}
            </p>
            {fechaCreacionTxt && (
              <p
                style={{
                  margin: '8px 0 0',
                  fontSize: 13,
                  fontWeight: 600,
                  color: GRAY,
                  letterSpacing: 0.4,
                }}
              >
                Creada el {fechaCreacionTxt}
              </p>
            )}
          </div>

          {/* Valor por número */}
          <div
            style={{
              width: 140,
              height: 140,
              borderRadius: '50%',
              background: `linear-gradient(160deg, ${BLUE} 0%, ${NAVY} 100%)`,
              color: '#fff',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: 10,
              flexShrink: 0,
              boxShadow: '0 6px 18px rgba(15,59,102,0.28)',
              border: '4px solid #fff',
            }}
          >
            {!esSorteo && (
              <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2, opacity: 0.95, letterSpacing: 0.5 }}>
                VALOR POR
                <br />
                NÚMERO
              </span>
            )}
            <span style={{ fontSize: esSorteo ? 22 : 22, fontWeight: 900, marginTop: esSorteo ? 0 : 4 }}>
              {precioTxt}
            </span>
          </div>
        </div>
      </div>

      {/* Banda de premios */}
      <div
        style={{
          background: SOFT,
          padding: '22px 40px 6px',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 900,
            color: NAVY,
            letterSpacing: 1.5,
          }}
        >
          {premios.length > 1 ? `¡${premios.length} GRANDES PREMIOS!` : '¡GRAN PREMIO!'}
        </p>
        <div
          style={{
            margin: '10px auto 0',
            height: 3,
            width: 120,
            background: BLUE,
            borderRadius: 2,
          }}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${premios.length}, 1fr)`,
          gap: 20,
          padding: '20px 40px 32px',
          background: SOFT,
        }}
      >
        {premios.map((premio, index) => {
          const img = urlImagenPremio(rifa, index);
          const badgeBg = index === 0 ? NAVY : index === 1 ? BLUE : LIGHT_BLUE;
          return (
            <div key={index} style={{ textAlign: 'center', position: 'relative' }}>
              <div
                style={{
                  position: 'absolute',
                  top: -2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  padding: '6px 18px',
                  borderRadius: 999,
                  background: badgeBg,
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: 1,
                  border: '3px solid #fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
                }}
              >
                {index + 1}° PREMIO
              </div>
              <div
                style={{
                  marginTop: 22,
                  height: 210,
                  background: '#fff',
                  borderRadius: 14,
                  border: `2px solid ${LIGHT_BLUE}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  padding: 10,
                  boxShadow: '0 4px 12px rgba(15,59,102,0.08)',
                }}
              >
                {img ? (
                  <img
                    src={img}
                    alt={premio}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <span style={{ fontSize: 40, opacity: 0.35 }}>🎁</span>
                )}
              </div>
              <p
                style={{
                  margin: '14px 0 0',
                  fontSize: 14,
                  fontWeight: 800,
                  color: NAVY,
                  textTransform: 'uppercase',
                  lineHeight: 1.3,
                  letterSpacing: 0.4,
                }}
              >
                {premio}
              </p>
            </div>
          );
        })}
      </div>

      {/* Grilla de números */}
      <div style={{ padding: '24px 40px 20px', background: '#fff' }}>
        <p
          style={{
            textAlign: 'center',
            fontSize: 30,
            fontWeight: 900,
            margin: '0 0 6px',
            color: NAVY,
            letterSpacing: 4,
            fontFamily: "'Georgia', 'Times New Roman', serif",
          }}
        >
          ELEGÍ TU NÚMERO
        </p>
        <p
          style={{
            textAlign: 'center',
            margin: '0 0 16px',
            fontSize: 13,
            color: GRAY,
            fontWeight: 600,
            letterSpacing: 0.4,
          }}
        >
          {total} números · {disponibles} disponible{disponibles !== 1 ? 's' : ''}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 8,
            background: SOFT,
            padding: 16,
            borderRadius: 12,
            border: `2px solid ${LIGHT_BLUE}`,
          }}
        >
          {numeros.map((num) => {
            const estado = estadoNumero(num, rifa, numerosGanadores);
            const label = String(num).padStart(2, '0');
            return (
              <div
                key={num}
                style={{
                  minHeight: 46,
                  border: `2px solid ${estado === 'libre' ? '#cfd8e0' : LIGHT_BLUE}`,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 17,
                  fontWeight: 800,
                  color: NAVY,
                  background: '#fff',
                }}
              >
                {estado === 'ganador' ? '👑' : estado === 'ocupado' ? <TrebolOcupado /> : label}
              </div>
            );
          })}
        </div>
        <p
          style={{
            textAlign: 'center',
            margin: '14px 0 0',
            fontSize: 12,
            color: GRAY,
            fontWeight: 600,
          }}
        >
          Trébol = vendido o reservado
        </p>
      </div>

      {(rifa.alias || rifa.cbu) && (
        <div
          style={{
            textAlign: 'center',
            padding: '16px 40px',
            background: SOFT,
            fontSize: 17,
            fontWeight: 800,
            color: NAVY,
            borderTop: `2px solid ${LIGHT_BLUE}`,
            borderBottom: `2px solid ${LIGHT_BLUE}`,
          }}
        >
          {rifa.alias && <span>Alias: {rifa.alias}</span>}
          {rifa.alias && rifa.cbu && <span style={{ color: GRAY, margin: '0 12px' }}> · </span>}
          {rifa.cbu && <span>CBU: {rifa.cbu}</span>}
        </div>
      )}

      <div
        style={{
          background: NAVY_DARK,
          color: '#cfe5ff',
          textAlign: 'center',
          padding: '14px',
          fontSize: 15,
          fontFamily: 'monospace',
          fontWeight: 700,
          letterSpacing: 0.6,
        }}
      >
        acceso7.com.ar/rifa/{rifa.id}
      </div>
    </div>
  );
});

export default RifaFlyerCompartir;
